import { animate, state, style, transition, trigger } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import * as zxcvbn from 'zxcvbn';
import { Config } from '../../../../config';
import { PasswordState } from '../../interfaces/password-state.interface';
import {
  dropboxScoreValidator,
  hasDropboxScoreError,
} from '../../validators/dropbox-score.validator';
import { equalityValidator, hasEqualityError } from '../../validators/equality.validator';
import {
  hasPasswordStrengthError,
  passwordStrengthValidator,
} from '../../validators/password-strength.validator';

const defaultPasswordState: PasswordState = {
  allowed: false,
  level: null,
  message: null,
};

@UntilDestroy()
@Component({
  selector: 'lgn-new-password',
  templateUrl: './new-password.component.html',
  styleUrls: ['./new-password.component.scss'],
  animations: [
    trigger('EnterLeave', [
      state('flyIn', style({ height: '72px', marginTop: '0', opacity: 1 })),
      transition(':enter', [
        style({ height: '20px', marginTop: '-20px', opacity: 0 }),
        animate('300ms ease-in'),
      ]),
      transition(':leave', [
        animate('250ms ease-out', style({ height: '20px', marginTop: '-20px', opacity: 0 })),
      ]),
    ]),
  ],
})
export class NewPasswordComponent implements OnInit {
  readonly minPasswordLength = 8;
  readonly minDropboxScore = 3;

  @Input() passwordLabel: string;
  @Input() passwordFieldName: string;
  @Input() retypedPasswordLabel: string;
  @Input() retypedPasswordFieldName: string;

  @Output() valueChanges = new EventEmitter<string>();
  @Output() validChanges = new EventEmitter<boolean>();
  @Output() enteredValidPassword = new EventEmitter<boolean>();

  passwordControl = new FormControl('', [
    passwordStrengthValidator,
    dropboxScoreValidator(this.minDropboxScore),
  ]);

  retypePasswordControl = new FormControl(
    { value: '', disabled: true },
    equalityValidator(this.passwordControl)
  );

  passwordState = defaultPasswordState;

  get passwordNotMatchErrorVisible() {
    const errors = this.retypePasswordControl.errors;
    return errors && !errors.required && hasEqualityError(errors);
  }

  constructor(public config: Config, private http: HttpClient) {}

  ngOnInit() {
    this.passwordControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => this.onPasswordChange(value));

    this.passwordControl.valueChanges
      .pipe(
        debounceTime(500),
        filter(() => this.passwordState.allowed),
        untilDestroyed(this)
      )
      .subscribe((password) => this.checkPasswordBanList(password));

    combineLatest([this.passwordControl.valueChanges, this.retypePasswordControl.valueChanges])
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.validChanges.emit(this.passwordControl.valid && this.retypePasswordControl.valid);
      });
  }

  onPasswordChange(value: string) {
    this.valueChanges.emit(value);
    const passwordState = this.analyzePassword();
    this.setPasswordState(passwordState);
  }

  passwordEnter() {
    if (this.passwordState?.allowed && !this.retypePasswordControl.invalid) {
      this.enteredValidPassword.emit(true);
    }
  }

  analyzePassword(): PasswordState {
    const errors = this.passwordControl.errors;

    if (errors?.required) {
      return defaultPasswordState;
    }

    if (errors?.minlength) {
      return {
        allowed: false,
        level: 'level-20',
        message:
          `Too short! Your password must be at least ` +
          `${errors.minlength.requiredLength} characters long.`,
      };
    }

    if (errors && hasPasswordStrengthError(errors)) {
      return { allowed: false, level: 'level-40', message: errors.passwordStrength };
    }

    if (errors && hasDropboxScoreError(errors)) {
      return {
        allowed: false,
        level: 'level-60',
        message: `Close but still too easy to guess.
         Try making it longer with a mix of symbols, numbers, upper case letters,
         and lower case letters.`,
      };
    }

    const password = this.passwordControl.value;
    const checkScoreResult = zxcvbn(password);
    const incredibleScore = 4;
    if (checkScoreResult.score < incredibleScore) {
      return { allowed: true, level: 'level-80', message: 'Good! This password is strong.' };
    } else {
      return {
        allowed: true,
        level: 'level-100',
        message: 'Incredible! This password is very strong.',
      };
    }
  }

  checkPasswordBanList(password: string) {
    this.http
      .post(this.config.passwordBanCheckUrl, { password })
      .subscribe((response: { banned: boolean }) => {
        if (!response.banned) {
          return;
        }

        this.setPasswordState({
          allowed: false,
          level: 'level-60',
          message: 'This password is compromised online. Please use something else',
        });
      });
  }

  setPasswordState(passwordState: PasswordState) {
    this.passwordState = passwordState;
    this.validChanges.emit(passwordState.allowed);

    if (passwordState.allowed) {
      this.retypePasswordControl.enable();
    } else {
      this.retypePasswordControl.disable();
    }
  }
}
